import { useState, useEffect, useCallback, useRef } from "react";
import {
  listSMTPConfigs,
  findSMTPConfigById,
  createSMTPConfig,
  updateSMTPConfig,
  deleteSMTPConfig,
  testSMTPConfig,
} from "../../../services/smtp.api";
import { toast } from "react-toastify";

/**
 * Hook personalizado para gestión de SMTP
 * Maneja todas las llamadas API y estados
 */
const useSMTPConfig = () => {
  const isMountedRef = useRef(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [testing, setTesting] = useState(false);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Obtener todas las configuraciones SMTP
   */
  const fetchConfigs = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await listSMTPConfigs();
      if (isMountedRef.current) {
        if (data.success) {
          setConfigs(data.data || []);
        } else {
          throw new Error(data.message || "Error obteniendo configuraciones SMTP");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error obteniendo configuraciones SMTP:", err);
        toast.error(errorMessage);
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  /**
   * Obtener configuración SMTP específica
   */
  const fetchConfig = useCallback(async (id) => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await findSMTPConfigById(id);
      if (isMountedRef.current) {
        if (data.success) {
          setSelectedConfig(data.data);
          return data.data;
        } else {
          throw new Error(data.message || "Error obteniendo configuración SMTP");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error obteniendo configuración SMTP:", err);
        toast.error(errorMessage);
      }
      return null;
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  /**
   * Crear nueva configuración SMTP
   */
  const createConfig = useCallback(async (configData) => {
    if (!isMountedRef.current) return null;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await createSMTPConfig(configData);
      if (isMountedRef.current) {
        if (data.success) {
          toast.success(data.message || "Configuración SMTP creada exitosamente");
          await fetchConfigs(); // Refrescar lista
          return data.data;
        } else {
          throw new Error(data.message || "Error creando configuración SMTP");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error creando configuración SMTP:", err);
        toast.error(errorMessage);
      }
      return null;
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [fetchConfigs]);

  /**
   * Actualizar configuración SMTP
   */
  const updateConfig = useCallback(async (id, configData) => {
    if (!isMountedRef.current) return null;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await updateSMTPConfig(id, configData);
      if (isMountedRef.current) {
        if (data.success) {
          toast.success(data.message || "Configuración SMTP actualizada exitosamente");
          await fetchConfigs(); // Refrescar lista
          if (selectedConfig && selectedConfig.id === id) {
            await fetchConfig(id); // Actualizar configuración seleccionada
          }
          return data.data;
        } else {
          throw new Error(data.message || "Error actualizando configuración SMTP");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error actualizando configuración SMTP:", err);
        toast.error(errorMessage);
      }
      return null;
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [fetchConfigs, fetchConfig, selectedConfig]);

  /**
   * Eliminar configuración SMTP (soft delete)
   */
  const deleteConfig = useCallback(async (id) => {
    if (!isMountedRef.current) return false;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await deleteSMTPConfig(id);
      if (isMountedRef.current) {
        if (data.success) {
          toast.success(data.message || "Configuración SMTP eliminada exitosamente");
          await fetchConfigs(); // Refrescar lista
          if (selectedConfig && selectedConfig.id === id) {
            setSelectedConfig(null);
          }
          return true;
        } else {
          throw new Error(data.message || "Error eliminando configuración SMTP");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error eliminando configuración SMTP:", err);
        toast.error(errorMessage);
      }
      return false;
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [fetchConfigs, selectedConfig]);

  /**
   * Probar conexión SMTP
   * Retorna resultado sin mostrar toasts (el modal se encarga de mostrar el resultado)
   */
  const testConnection = useCallback(async (id) => {
    if (!isMountedRef.current) return { success: false, message: "" };
    try {
      if (isMountedRef.current) setTesting(true);
      if (isMountedRef.current) setError(null);
      const { data } = await testSMTPConfig(id);
      if (isMountedRef.current) {
        if (data.success) {
          return { success: true, message: data.message || "Conexión SMTP exitosa" };
        } else {
          return { success: false, message: data.message || "Error probando conexión SMTP" };
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error probando conexión SMTP:", err);
        return { success: false, message: errorMessage };
      }
    } finally {
      if (isMountedRef.current) setTesting(false);
    }
    return { success: false, message: "" };
  }, []);

  // Cargar configuraciones al montar
  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  return {
    loading,
    error,
    configs,
    selectedConfig,
    testing,
    fetchConfigs,
    fetchConfig,
    createConfig,
    updateConfig,
    deleteConfig,
    testConnection,
    setSelectedConfig
  };
};

export default useSMTPConfig;
